import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import translate from "@insite/client-framework/Translate";
import { ProductPriceDto } from "@insite/client-framework/Types/ApiModels";
import { ButtonIcon } from "@insite/mobius/Button";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Dollar from "@WhiteCap/Icons/Dollar";
import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { css } from "styled-components";

interface OwnProps {
    pricing: ProductPriceDto;
    qtyOrdered: number | null;
    unitOfMeasure?: string;
    productNumber?: string;
    mfg?: string;
}

export interface PriceDetailsStyles {
    parentWrapper?: InjectableCss;
    topContainer?: InjectableCss;
    closeIconWrapper?: InjectableCss;
    bottomContainer?: InjectableCss;
    firstRow?: InjectableCss;
    secondRow?: InjectableCss;
    container?: InjectableCss;
    closeIcon?: IconPresentationProps;
    priceDetailsLink?: LinkPresentationProps;
    priceDetailsLinkWrapper?: InjectableCss;
    productInfoText?: TypographyProps;
    text?: TypographyProps;
    deatilsLink?: TypographyProps;
}

export const priceDetailsStyles: PriceDetailsStyles = {
    parentWrapper: {
        css: css`
            position: fixed;
            box-sizing: border-box;
            background-color: white;
            outline: 1px solid #ccc;
            box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.15);
            z-index: ${getProp("theme.zIndex.menu")};
            display: flex;
            flex-direction: column;
            padding: 0px 16px 24px 16px;
            transition: opacity 0.3s ease-in-out;
            transform: translateZ(0);
            width: 360px;
            @media screen and (max-width: 380px) {
                overflow-x: auto;
                width: 95vw;
                white-space: nowrap;
            }
        `,
    },
    topContainer: {
        css: css`
            position: relative;
            display: flex;
            flex-direction: column;
            padding: 16px 0px 8px 0px;
            overflow-x: auto;
            white-space: nowrap;
        `,
    },
    bottomContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            overflow-x: auto;
            white-space: nowrap;
        `,
    },
    firstRow: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 100%;
            gap: 8px;
        `,
    },
    secondRow: {
        css: css`
            display: flex;
            flex-direction: row;
            width: 100%;
            gap: 8px;
        `,
    },
    container: {
        css: css`
            width: 50%;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            background-color: #f7f8f9;
            padding 4px 0px;
        `,
    },
    closeIconWrapper: {
        css: css`
            position: absolute;
            top: 16px;
            right: 0;
            border-radius: 4px;
            transition: background-color 0.2s ease;

            &:active {
                background-color: rgba(0, 0, 0, 0.3); // Slight dark overlay
            }
        `,
    },
    closeIcon: {
        size: 24,
    },
    priceDetailsLink: {
        css: css`
            width: fit-content;
            &:focus {
                outline: none;
            }
        `,
        typographyProps: {
            css: css`
                font-size: 12px;
                font-style: normal;
                font-weight: 425;
                line-height: 16px;
                colorr: #2f63b0;
            `,
        },
    },
    priceDetailsLinkWrapper: {
        css: css`
            position: absolute;
            top: 6px;
            right: 8px;
            display: inline-flex;
            align-items: center;
            gap: 8px;
        `,
    },
    text: {
        css: css`
            text-align: center;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 16px;
        `,
    },
    productInfoText: {
        css: css`
            font-size: 14px;
            font-weight: 400;
            line-height: 24px;
        `,
    },
};

const styles = priceDetailsStyles;

const PriceDetails: React.FC<OwnProps> = ({ pricing, qtyOrdered, productNumber, mfg, unitOfMeasure }) => {
    // Local state to manage modal open/close
    const [isModalIsOpen, setIsModalIsOpen] = useState(false);
    const triggerRef = useRef<HTMLDivElement>(null);
    const modalRef = useRef<HTMLDivElement>(null);
    const hasClosed = useRef(false);
    const [alignCenter, setAlignCenter] = useState(false);

    // Update mobile state on resize
    useEffect(() => {
        const updateAlignCenter = () => setAlignCenter(window.innerWidth <= 500);
        updateAlignCenter();
        window.addEventListener("resize", updateAlignCenter);
        return () => window.removeEventListener("resize", updateAlignCenter);
    }, []);

    const updateModalPosition = (shouldAdjustPosition = false) => {
        if (!triggerRef.current || !modalRef.current) {
            return;
        }

        const trigger = triggerRef.current;
        const modal = modalRef.current;

        const triggerRect = trigger.getBoundingClientRect();
        const modalRect = modal.getBoundingClientRect();

        const modalWidth = modalRect.width;

        // Align modal's right edge with trigger's right edge
        let top = triggerRect.bottom + 2;
        let left;
        if (window.innerWidth <= 768) {
            left = triggerRect.right - modalWidth;
        } else {
            left = triggerRect.right - modalWidth + 20;
        }

        // Prevent horizontal overflow
        if (!alignCenter && left + modalWidth > window.innerWidth) {
            left = window.innerWidth - modalWidth;
        }

        // Prevent modal from going off left side of screen
        if (left < 0) {
            left = 0;
        }

        // Set modal styles
        modal.style.position = "fixed";
        modal.style.top = `${top}px`;

        if (!alignCenter) {
            modal.style.left = `${left}px`;
            modal.style.transform = "";
        }

        if (!shouldAdjustPosition) {
            handleInitialOverflow(modal, left, top, modalWidth);
        }
    };

    const handleInitialOverflow = (modal: HTMLElement, left: number, top: number, modalWidth: number) => {
        const topOverflowAmount = Math.abs(top); // Amount the modal overflows at the top
        const modalRight = left + modalWidth;

        if (alignCenter) {
            // Mobile: Center horizontally and adjust top overflow if needed
            modal.style.left = "50%";
            modal.style.transform = `translateX(-50%) ${
                top < 0 ? `translateY(${topOverflowAmount}px)` : "translateY(0px)"
            }`;
        } else {
            // Desktop: Adjust for top and right overflow
            let transform = "";

            if (modalRight > window.innerWidth) {
                const overflowAmountX = modalRight - window.innerWidth; // Calculate horizontal overflow
                transform += `translateX(-${overflowAmountX}px)`;
            }

            modal.style.transform = transform.trim();
        }
    };

    useEffect(() => {
        if (isModalIsOpen) {
            updateModalPosition();
            const handleScroll = () => updateModalPosition(true);
            const handleResize = () => updateModalPosition(true);

            window.addEventListener("scroll", handleScroll);
            window.addEventListener("resize", handleResize);

            return () => {
                window.removeEventListener("scroll", handleScroll);
                window.removeEventListener("resize", handleResize);
            };
        }
    }, [isModalIsOpen, alignCenter]);

    // Close modal on outside click
    useEffect(() => {
        const handleClickOutside = (event: PointerEvent) => {
            if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
                if (hasClosed.current) {
                    return;
                }
                hasClosed.current = true;
                setIsModalIsOpen(false);
            }
        };

        if (isModalIsOpen) {
            document.addEventListener("pointerdown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("pointerdown", handleClickOutside);
            hasClosed.current = false;
        };
    }, [isModalIsOpen]);

    // Close modal on ESC key
    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isModalIsOpen) {
                setIsModalIsOpen(false);
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [isModalIsOpen]);

    useEffect(() => {
        if (isModalIsOpen) {
            hasClosed.current = false;
        }
    }, [isModalIsOpen]);

    const { price: unitNetPrice, priceDisplay: unitNetPriceDisplay } = getUnitNetPrice(pricing, qtyOrdered || 1);

    const unitCost = pricing.unitCost;

    const unitCostDisplay = pricing.unitCostDisplay;

    const getFormattedMargin = () => {
        if (!unitNetPrice || isunitCostNullOrZero()) {
            return "--";
        }
        const discountPercent = ((unitNetPrice - unitCost) / unitNetPrice) * 100;
        return `${discountPercent.toFixed(2)}%`;
    };

    const isunitCostNullOrZero = () => {
        if (Number(unitCost) === 0 || unitCost === null || unitCost === undefined) {
            return true;
        }
        return false;
    };

    return (
        <>
            <Link onClick={() => setIsModalIsOpen(true)} {...styles.priceDetailsLink}>
                <StyledWrapper ref={triggerRef} {...styles.priceDetailsLinkWrapper} data-test-selector="price_details-wrapper">
                    <Icon src={Dollar} size={12} />
                    <Typography {...styles.priceDetailsLink}>{translate("Details")}</Typography>
                </StyledWrapper>
            </Link>

            {/* Modal rendered in portal */}
            {isModalIsOpen &&
                createPortal(
                    <StyledWrapper
                        ref={modalRef}
                        {...styles.parentWrapper}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="modal-title"
                        onClick={e => e.stopPropagation()}
                        tabIndex={-1}
                    >
                        <StyledWrapper {...styles.topContainer}>
                            <Typography as="p" {...styles.productInfoText}>
                                <Typography as="span">SKU# </Typography>
                                <Typography as="span" style={{ fontWeight: 500 }}>
                                    {productNumber}
                                </Typography>
                            </Typography>

                            <Typography as="p" {...styles.productInfoText}>
                                <Typography as="span">MFG# </Typography>
                                <Typography as="span" style={{ fontWeight: 500 }}>
                                    {mfg}
                                </Typography>
                            </Typography>

                            <ButtonIcon
                                {...styles.closeIconWrapper}
                                src={X}
                                onClick={() => setIsModalIsOpen(false)}
                                aria-label="Close modal"
                            />
                        </StyledWrapper>

                        <StyledWrapper {...styles.bottomContainer}>
                            <StyledWrapper {...styles.firstRow}>
                                <StyledWrapper {...styles.container}>
                                    <Typography {...styles.text}>Price</Typography>
                                    <Typography {...styles.text} style={{ fontWeight: 500 }}>
                                        {" "}
                                        {`${unitNetPriceDisplay}`}
                                    </Typography>
                                    <Typography {...styles.text}> &nbsp;({unitOfMeasure})</Typography>
                                </StyledWrapper>
                                <StyledWrapper {...styles.container}>
                                    <Typography {...styles.text}>Cost</Typography>
                                    {!isunitCostNullOrZero() ? (
                                        <>
                                            <Typography {...styles.text} style={{ fontWeight: 500 }}>
                                                {`${unitCostDisplay}`}
                                            </Typography>
                                            <Typography {...styles.text}>&nbsp;({unitOfMeasure})</Typography>
                                        </>
                                    ) : (
                                        <Typography {...styles.text} style={{ fontWeight: 500 }}>
                                            --
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                            <StyledWrapper {...styles.secondRow}>
                                <StyledWrapper {...styles.container}>
                                    <Typography {...styles.text}>Modifier</Typography>
                                    <Typography {...styles.text} style={{ fontWeight: 500 }}>
                                        {pricing.additionalResults?.modifierType
                                            ? pricing.additionalResults?.modifierType
                                            : "--"}
                                    </Typography>
                                </StyledWrapper>

                                <StyledWrapper {...styles.container}>
                                    <Typography {...styles.text}>Margin</Typography>
                                    <Typography {...styles.text} style={{ fontWeight: 500 }}>
                                        {getFormattedMargin()}
                                    </Typography>
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledWrapper>,
                    document.body,
                )}
        </>
    );
};

export default PriceDetails;
